import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import Logo from "./Logo";
import { SocialLinks, ContactEmails } from "./SocialIcons";

export default function Footer({ settings }) {
    if (!settings) return null;
    return (
        <footer className="bg-[#0F1A11] text-white/85 mt-16" data-testid="site-footer">
            <div className="lnl-container py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        <Logo size={44} />
                        <div className="font-heading font-bold text-white leading-tight">
                            {settings.official_name}
                            <div className="text-[10px] uppercase tracking-[0.18em] text-lnl-gold font-semibold">Est. {settings.established_year_draft}</div>
                        </div>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed">
                        A private higher-education institution serving learners in Urdaneta City, Pangasinan and Northern Luzon.
                    </p>
                    <div className="mt-5 space-y-2 text-sm">
                        <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-lnl-gold shrink-0" /> {settings.address_line}, {settings.province_line}</div>
                        <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-lnl-gold" /> <a className="hover:underline" href={`tel:${(settings.mobile||"").replace(/[^0-9+]/g, "")}`}>{settings.mobile}</a></div>
                        <ContactEmails settings={settings} Icon={Mail} iconClass="h-4 w-4 text-lnl-gold shrink-0" testid="footer-email" />
                    </div>
                    <div className="mt-5">
                        <SocialLinks settings={settings} testid="footer-socials" />
                    </div>
                </div>

                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-lnl-gold font-heading font-semibold mb-4">Admissions</div>
                    <ul className="space-y-2 text-sm">
                        {[
                            ["/admissions", "Admissions Overview"],
                            ["/admissions#applicant-paths", "Applicant Types"],
                            ["/admissions/requirements", "Requirements"],
                            ["/admissions/apply", "Apply Online"],
                            ["/tuition-scholarships", "Tuition & Scholarships"],
                            ["/faq", "FAQs"],
                        ].map(([href, label]) => (
                            <li key={label}><Link to={href} className="hover:text-lnl-gold-soft">{label}</Link></li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-lnl-gold font-heading font-semibold mb-4">Academics</div>
                    <ul className="space-y-2 text-sm">
                        {[
                            ["/academics", "Academic Departments"],
                            ["/academics?level=college", "College Programs"],
                            ["/academics/department-of-basic-education", "Basic Education"],
                            ["/admissions/senior-high", "Senior High School"],
                            ["/#program-finder", "Program Finder"],
                        ].map(([href, label]) => (
                            <li key={label}><Link to={href} className="hover:text-lnl-gold-soft">{label}</Link></li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-lnl-gold font-heading font-semibold mb-4">Resources</div>
                    <ul className="space-y-2 text-sm">
                        <li><a href={settings.portal_url} target="_blank" rel="noopener noreferrer" className="hover:text-lnl-gold-soft">Student Portal</a></li>
                        <li><a href={settings.library_url} target="_blank" rel="noopener noreferrer" className="hover:text-lnl-gold-soft">Library</a></li>
                        <li><a href={settings.opac_url} target="_blank" rel="noopener noreferrer" className="hover:text-lnl-gold-soft">OPAC</a></li>
                        <li><Link to="/mobile-app" className="hover:text-lnl-gold-soft">Mobile App</Link></li>
                        <li><Link to="/news" className="hover:text-lnl-gold-soft">News</Link></li>
                        <li><Link to="/contact" className="hover:text-lnl-gold-soft">Contact</Link></li>
                        <li><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.map_query || "Lyceum Northern Luzon Urdaneta")}`} target="_blank" rel="noopener noreferrer" className="hover:text-lnl-gold-soft" data-testid="footer-directions">Directions</a></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="lnl-container py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
                    <div>© {new Date().getFullYear()} {settings.official_name}. All rights reserved.</div>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link to="/legal/privacy" className="hover:text-white">Privacy Policy</Link>
                        <Link to="/legal/terms" className="hover:text-white">Terms of Use</Link>
                        <Link to="/legal/accessibility" className="hover:text-white">Accessibility</Link>
                        <Link to="/admin" className="hover:text-white opacity-60" data-testid="admin-footer-link">Staff</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
